import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, Users } from "lucide-react";
import { cn } from "@/cn";
import {
  deleteWorkspaceList,
  getWorkspaceList,
  removeListMembers,
} from "@/api/lists";

export function ListsDetailPage() {
  const { listId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedPeople, setSelectedPeople] = useState<Set<string>>(new Set());
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(
    new Set(),
  );
  const [q, setQ] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["workspace-list", listId],
    queryFn: () => getWorkspaceList(listId),
    enabled: !!listId,
  });

  const list = data?.list;
  const isCompanies = list?.modality === "companies";

  const removeMutation = useMutation({
    mutationFn: () =>
      removeListMembers(
        listId,
        isCompanies
          ? { accountIds: [...selectedCompanies] }
          : { leadIds: [...selectedPeople] },
      ),
    onSuccess: () => {
      setSelectedPeople(new Set());
      setSelectedCompanies(new Set());
      qc.invalidateQueries({ queryKey: ["workspace-list", listId] });
      qc.invalidateQueries({ queryKey: ["workspace-lists"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteWorkspaceList(listId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace-lists"] });
      navigate("/lists");
    },
  });

  const selectedCount = isCompanies
    ? selectedCompanies.size
    : selectedPeople.size;

  const findMoreHref = isCompanies ? "/companies" : "/people";

  const needle = q.trim().toLowerCase();
  const peopleRows = useMemo(() => {
    const rows = data?.peopleMembers ?? [];
    if (!needle) return rows;
    return rows.filter((m) => {
      const c = m.lead;
      return [c?.name, c?.title, c?.email, c?.accountId, m.leadId]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [data?.peopleMembers, needle]);
  const companyRows = useMemo(() => {
    const rows = data?.companyMembers ?? [];
    if (!needle) return rows;
    return rows.filter((m) => {
      const c = m.company;
      return [c?.name, c?.domain, c?.industry, m.accountId]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [data?.companyMembers, needle]);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading list…</p>;
  }
  if (error || !list) {
    return (
      <div>
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "List not found"}
        </p>
        <Link to="/lists" className="mt-2 inline-block text-sm text-brand">
          Back to Lists
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6">
        <Link
          to="/lists"
          className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Lists
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{list.name}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5">
                {isCompanies ? (
                  <Building2 className="size-3" />
                ) : (
                  <Users className="size-3" />
                )}
                {isCompanies ? "Companies" : "People"}
              </span>
              <span>{list.memberCount} members</span>
              <span>
                Updated {new Date(list.updatedAt).toLocaleString()}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={
                isCompanies ? "Search companies…" : "Search people…"
              }
              className="w-48 rounded-md border border-border bg-background px-3 py-1.5 text-xs"
            />
            <Link
              to={findMoreHref}
              className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground"
            >
              Find more
            </Link>
            <button
              type="button"
              disabled={selectedCount === 0 || removeMutation.isPending}
              onClick={() => removeMutation.mutate()}
              className="rounded-md border border-border px-3 py-1.5 text-xs disabled:opacity-40"
            >
              Remove selected
            </button>
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    `Delete list “${list.name}”? This cannot be undone.`,
                  )
                ) {
                  deleteMutation.mutate();
                }
              }}
              className="rounded-md border border-border px-3 py-1.5 text-xs text-destructive"
            >
              Delete list
            </button>
          </div>
        </div>
      </div>

      {isCompanies ? (
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/10 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="w-9 px-2 py-2">
                  <input
                    type="checkbox"
                    checked={
                      companyRows.length > 0 &&
                      companyRows.every((r) =>
                        selectedCompanies.has(r.accountId),
                      )
                    }
                    onChange={(e) =>
                      setSelectedCompanies(
                        e.target.checked
                          ? new Set(companyRows.map((r) => r.accountId))
                          : new Set(),
                      )
                    }
                  />
                </th>
                <th className="px-2 py-2 font-semibold">Company</th>
                <th className="px-2 py-2 font-semibold">Domain</th>
                <th className="px-2 py-2 font-semibold">Industry</th>
                <th className="px-2 py-2 font-semibold"># Emp</th>
              </tr>
            </thead>
            <tbody>
              {companyRows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-10 text-center text-xs text-muted-foreground"
                  >
                    No companies yet. Save from Find companies.
                  </td>
                </tr>
              )}
              {companyRows.map((row) => {
                const c = row.company;
                const selected = selectedCompanies.has(row.accountId);
                return (
                  <tr
                    key={row.accountId}
                    className={cn(
                      "border-b border-border hover:bg-muted/10",
                      selected && "bg-brand-soft/40",
                    )}
                  >
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() =>
                          setSelectedCompanies((prev) => {
                            const next = new Set(prev);
                            if (next.has(row.accountId))
                              next.delete(row.accountId);
                            else next.add(row.accountId);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Link
                        to={`/user/accounts/${row.accountId}`}
                        className="font-semibold text-brand hover:underline"
                      >
                        {c?.name ?? row.accountId}
                      </Link>
                    </td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">
                      {c?.domain ?? "—"}
                    </td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">
                      {c?.industry ?? "—"}
                    </td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">
                      {c?.employeeCount?.toLocaleString() ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/10 text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="w-9 px-2 py-2">
                  <input
                    type="checkbox"
                    checked={
                      peopleRows.length > 0 &&
                      peopleRows.every((r) => selectedPeople.has(r.leadId))
                    }
                    onChange={(e) =>
                      setSelectedPeople(
                        e.target.checked
                          ? new Set(peopleRows.map((r) => r.leadId))
                          : new Set(),
                      )
                    }
                  />
                </th>
                <th className="px-2 py-2 font-semibold">Name</th>
                <th className="px-2 py-2 font-semibold">Title</th>
                <th className="px-2 py-2 font-semibold">Company</th>
                <th className="px-2 py-2 font-semibold">Email</th>
              </tr>
            </thead>
            <tbody>
              {peopleRows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-10 text-center text-xs text-muted-foreground"
                  >
                    No people yet. Save from Find people.
                  </td>
                </tr>
              )}
              {peopleRows.map((row) => {
                const c = row.lead;
                const selected = selectedPeople.has(row.leadId);
                return (
                  <tr
                    key={row.leadId}
                    className={cn(
                      "border-b border-border hover:bg-muted/10",
                      selected && "bg-brand-soft/40",
                    )}
                  >
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() =>
                          setSelectedPeople((prev) => {
                            const next = new Set(prev);
                            if (next.has(row.leadId))
                              next.delete(row.leadId);
                            else next.add(row.leadId);
                            return next;
                          })
                        }
                      />
                    </td>
                    <td className="px-2 py-2 font-semibold">
                      {c?.name ?? row.leadId}
                    </td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">
                      {c?.title ?? "—"}
                    </td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">
                      {c?.accountId ? (
                        <Link
                          to={`/user/accounts/${c.accountId}`}
                          className="text-brand hover:underline"
                        >
                          {c.accountId}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-2 py-2 text-xs text-muted-foreground">
                      {c?.email ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
